import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Search, SlidersHorizontal, X, Loader2, Music, Ticket, Zap } from "lucide-react";
import EventCard from "../components/EventCard";
import SkeletonCard from "../components/SkeletonCard";
import StickySearchHeader from "../components/StickySearchHeader";
import CitySearchAutocomplete from "../components/CitySearchAutocomplete";
import Footer from "../components/Footer";
import { toast } from "sonner";
import { haptic } from "../utils/haptic";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EVENTS_PER_PAGE = 20;

// Source badges for multi-source aggregation
const SOURCE_BADGES = {
  resident_advisor: { label: "CURATED", color: "text-primary", bg: "bg-primary/10" },
  dice: { label: "DICE", color: "text-secondary", bg: "bg-secondary/10" },
  songkick: { label: "SK", color: "text-accent", bg: "bg-accent/10" },
  ticketmaster: { label: "TM", color: "text-blue-400", bg: "bg-blue-400/10" },
  xceed: { label: "XC", color: "text-primary", bg: "bg-primary/10" },
  xtrip: { label: "XT", color: "text-primary", bg: "bg-primary/10" },
};

// Scene score badges for underground filter
const getSceneBadge = (score) => {
  if (score >= 10) return { label: "LEGEND", color: "text-yellow-400", bg: "bg-yellow-400/20", icon: "⚡" };
  if (score >= 7) return { label: "CERTIFIED", color: "text-purple-400", bg: "bg-purple-400/20", icon: "★" };
  if (score >= 4) return { label: "UNDERGROUND", color: "text-green-400", bg: "bg-green-400/20", icon: "◉" };
  if (score >= 3) return { label: "APPROVED", color: "text-blue-400", bg: "bg-blue-400/20", icon: "✓" };
  return null;
};

const Events = ({ onSignUpClick }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || searchParams.get('artist') || '');
  const [genre, setGenre] = useState(searchParams.get('genre') || '');
  const [city, setCity] = useState(searchParams.get('city') || '');
  const [minPrice, setMinPrice] = useState(searchParams.get('min_price') || '');
  const [maxPrice, setMaxPrice] = useState(searchParams.get('max_price') || '');
  const [source, setSource] = useState(searchParams.get('source') || '');

  // Infinite scroll observer
  const observerRef = useRef(null);
  const loadMoreRef = useRef(null);

  // Check viewport size
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Build API params
  const buildParams = useCallback((pageNum = 1) => {
    const params = new URLSearchParams();
    if (searchQuery) params.append('artist', searchQuery);
    if (genre && genre !== 'all') params.append('genre', genre);
    if (city) params.append('city', city);
    if (minPrice) params.append('min_price', minPrice);
    if (maxPrice) params.append('max_price', maxPrice);
    if (source && source !== 'all') params.append('source', source);
    params.append('limit', EVENTS_PER_PAGE.toString());
    params.append('skip', ((pageNum - 1) * EVENTS_PER_PAGE).toString());
    return params;
  }, [searchQuery, genre, city, minPrice, maxPrice, source]);

  // Fetch events
  const fetchEvents = useCallback(async (pageNum = 1, append = false) => {
    if (pageNum === 1) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const params = buildParams(pageNum);
      const response = await fetch(`${API_URL}/api/events?${params.toString()}`);
      const data = await response.json();
      
      if (data.events && data.events.length > 0) {
        if (append) {
          setEvents(prev => [...prev, ...data.events]);
        } else {
          setEvents(data.events);
        }
        setTotal(data.total || data.events.length);
        setHasMore(data.events.length === EVENTS_PER_PAGE);
      } else if (pageNum === 1) {
        // Seed if empty
        await fetch(`${API_URL}/api/seed`, { method: 'POST' });
        const retryResponse = await fetch(`${API_URL}/api/events?${params.toString()}`);
        const retryData = await retryResponse.json();
        setEvents(retryData.events || []);
        setTotal(retryData.total || 0);
        setHasMore(retryData.events?.length === EVENTS_PER_PAGE);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Failed to load experiences');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [buildParams]);

  // Initial load and filter changes
  useEffect(() => {
    setPage(1);
    fetchEvents(1, false);
  }, [searchParams, fetchEvents]);

  // Infinite scroll observer
  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMore && !loading && !loadingMore) {
          const nextPage = page + 1;
          setPage(nextPage);
          fetchEvents(nextPage, true);
        }
      },
      { threshold: 0.1, rootMargin: "100px" }
    );

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, loading, loadingMore, page, fetchEvents]);

  const applyFilters = () => {
    haptic("light");
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (genre && genre !== 'all') params.set('genre', genre);
    if (city) params.set('city', city);
    if (minPrice) params.set('min_price', minPrice);
    if (maxPrice) params.set('max_price', maxPrice);
    if (source && source !== 'all') params.set('source', source);
    setSearchParams(params);
    setShowFilters(false);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setGenre('all');
    setCity('');
    setMinPrice('');
    setMaxPrice('');
    setSource('all');
    setSearchParams({});
  };

  const hasActiveFilters = searchQuery || (genre && genre !== 'all') || city || minPrice || maxPrice || (source && source !== 'all');

  // Get unique sources from events
  const sources = [...new Set(events.map(e => e.source).filter(Boolean))];

  return (
    <div className="min-h-screen bg-background pt-24 pb-16">
      {/* Sticky Search Header */}
      <StickySearchHeader scrollThreshold={200} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-2">
            Curated Experiences
          </h1>
          <p className="text-muted-foreground">
            Hand-picked moments across Europe&apos;s most iconic venues
          </p>
          {/* Multi-source indicator */}
          <div className="flex flex-wrap items-center gap-2 mt-4">
            <span className="text-xs text-muted-foreground uppercase tracking-wider">Sources:</span>
            {Object.entries(SOURCE_BADGES).map(([key, badge]) => (
              <span 
                key={key}
                className={`px-2 py-0.5 rounded-full text-[10px] font-mono uppercase ${badge.bg} ${badge.color}`}
              >
                {badge.label}
              </span>
            ))}
          </div>
        </div>

        {/* Search & Filters Bar */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search events, artists, DJs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
              onFocus={() => haptic("light")}
              className="pl-10 bg-card border-border h-12"
              data-testid="events-search-input"
            />
          </div>
          <Button
            variant="outline"
            onClick={() => {
              haptic("light");
              setShowFilters(!showFilters);
            }}
            className={`border-border ${showFilters ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-primary'}`}
            data-testid="toggle-filters-btn"
          >
            <SlidersHorizontal className="w-4 h-4 mr-2" />
            Filters
            {hasActiveFilters && (
              <span className="ml-2 w-5 h-5 bg-primary text-primary-foreground text-xs flex items-center justify-center">
                !
              </span>
            )}
          </Button>
          <Button
            onClick={applyFilters}
            className="bg-primary text-primary-foreground hover:bg-primary/90 btn-glow uppercase tracking-wider"
            data-testid="apply-search-btn"
          >
            Search
          </Button>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="bg-card border border-border p-6 mb-8 animate-fade-in-up" style={{ animationFillMode: 'forwards' }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-foreground uppercase tracking-wider">Filters</h3>
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="text-muted-foreground hover:text-accent"
                  data-testid="clear-filters-btn"
                >
                  <X className="w-4 h-4 mr-1" />
                  Clear All
                </Button>
              )}
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">Genre</label>
                <Select value={genre} onValueChange={setGenre}>
                  <SelectTrigger className="bg-input border-border" data-testid="genre-select">
                    <SelectValue placeholder="All Genres" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Genres</SelectItem>
                    <SelectItem value="Techno">Techno</SelectItem>
                    <SelectItem value="House">House</SelectItem>
                    <SelectItem value="Electronica">Electronica</SelectItem>
                    <SelectItem value="Minimal">Minimal</SelectItem>
                    <SelectItem value="Trance">Trance</SelectItem>
                    <SelectItem value="Drum & Bass">Drum & Bass</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">City</label>
                <CitySearchAutocomplete
                  value={city}
                  onChange={setCity}
                  onSelect={(cityData) => setCity(cityData.city)}
                  placeholder="Any city"
                  navigateOnSelect={false}
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">Source</label>
                <Select value={source} onValueChange={setSource}>
                  <SelectTrigger className="bg-input border-border" data-testid="source-select">
                    <SelectValue placeholder="All Sources" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sources</SelectItem>
                    <SelectItem value="xceed">Xceed (Live)</SelectItem>
                    <SelectItem value="dice">Dice.fm</SelectItem>
                    <SelectItem value="songkick">Songkick</SelectItem>
                    <SelectItem value="ticketmaster">Ticketmaster</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">Min Price</label>
                <Input
                  type="number"
                  placeholder="£0"
                  value={minPrice}
                  onChange={(e) => setMinPrice(e.target.value)}
                  className="bg-input border-border font-mono"
                  data-testid="min-price-input"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">Max Price</label>
                <Input
                  type="number"
                  placeholder="£1000"
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(e.target.value)}
                  className="bg-input border-border font-mono"
                  data-testid="max-price-input"
                />
              </div>
            </div>
          </div>
        )}

        {/* Results Count */}
        <div className="flex items-center justify-between mb-6">
          <span className="text-sm text-muted-foreground">
            <span className="font-mono text-primary">{total}</span> events found
            {events.length < total && (
              <span className="ml-2">
                (showing <span className="font-mono text-secondary">{events.length}</span>)
              </span>
            )}
          </span>
          {sources.length > 1 && (
            <div className="hidden sm:flex items-center gap-1">
              {sources.map(s => (
                <span key={s} className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${SOURCE_BADGES[s]?.bg || 'bg-muted'} ${SOURCE_BADGES[s]?.color || 'text-muted-foreground'}`}>
                  {SOURCE_BADGES[s]?.label || s}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Events Grid / Feed */}
        {loading ? (
          <>
            {/* Mobile Skeleton - Feed View */}
            <div className="md:hidden space-y-3">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <SkeletonCard key={i} variant="feed" />
              ))}
            </div>
            {/* Desktop Skeleton - Grid View */}
            <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <SkeletonCard key={i} variant="default" />
              ))}
            </div>
          </>
        ) : events.length > 0 ? (
          <>
            {/* Mobile Feed - Vertical Scrollable */}
            <div className="md:hidden space-y-3">
              {events.map((event) => (
                <EventCard key={event.event_id} event={event} variant="feed" />
              ))}
            </div>
            {/* Desktop Grid */}
            <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="events-grid">
              {events.map((event, index) => (
                <EventCard key={event.event_id} event={event} index={index} variant="grid" />
              ))}
            </div>

            {/* Infinite Scroll Trigger */}
            <div 
              ref={loadMoreRef} 
              className="w-full py-8 flex items-center justify-center"
            >
              {loadingMore ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span className="font-mono text-sm">Loading more experiences...</span>
                </div>
              ) : hasMore ? (
                <div className="flex items-center gap-2 text-muted-foreground/50">
                  <Zap className="w-4 h-4" />
                  <span className="font-mono text-xs">Scroll for more</span>
                </div>
              ) : events.length > EVENTS_PER_PAGE ? (
                <div className="flex items-center gap-2 text-muted-foreground/50">
                  <Music className="w-4 h-4" />
                  <span className="font-mono text-xs">All {total} events loaded</span>
                </div>
              ) : null}
            </div>
          </>
        ) : (
          <div className="text-center py-16">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted/20 flex items-center justify-center">
              <Ticket className="w-8 h-8 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground mb-4">No events found matching your criteria.</p>
            <Button
              variant="outline"
              onClick={clearFilters}
              className="border-primary text-primary hover:bg-primary hover:text-primary-foreground"
            >
              Clear Filters
            </Button>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default Events;
